// The note's body: a CodeMirror editor over the plain text, with ```sheet
// blocks shown as grids, ```recipe blocks as cards, ```calc blocks with each
// line's result, `- [ ]` lines with checkboxes and web addresses as links.
import {
  checkAll as checkAllItems,
  parseItem,
  toggleItem,
} from '@/app/checklist/checklist';
import {
  defaultKeymap,
  history,
  historyKeymap,
} from '@codemirror/commands';
import {
  openSearchPanel,
  search,
  searchKeymap,
} from '@codemirror/search';
import {
  Annotation,
  EditorSelection,
  EditorState,
} from '@codemirror/state';
import {
  EditorView,
  keymap,
  placeholder,
} from '@codemirror/view';
import {
  type Ref,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { isMobile } from '../mobile';
import { PopupMenu } from '../PopupMenu';
import {
  blocks,
  startOutsideBlocks,
} from './blocks';
import {
  calcs,
  newCalc,
} from './calcs';
import {
  checklistItems,
  checklists,
} from './checklists';
import { formatDateTime } from './dateTime';
import { links } from './links';
import {
  newRecipe,
  recipeBlocks,
} from './recipes';
import {
  newSheet,
  sheetBlocks,
} from './sheets';

const blockKinds = [sheetBlocks, recipeBlocks];
const shownBlocks = blocks(blockKinds);

export interface EditorHandle {
  focus(): void;
  insertSheet(): void;
  insertRecipe(): void;
  insertCalc(): void;
  insertChecklist(): void;
  // null for a note without checkboxes, else whether all are checked
  checklistDone(): boolean | null;
  checkAll(checked: boolean): void;
  insertDateTime(): void;
  find(): void;
}

// marks a change that came in through `value`, not from typing, so it isn't
// reported back as an edit
const external = Annotation.define<boolean>();

// the find bar's words, in the app's own casing. Next and previous show as
// arrows, and keep their words for screen readers
const searchPhrases = {
  'next': 'Next match',
  'previous': 'Previous match',
  'all': 'Select all',
  'match case': 'Match case',
  'regexp': 'Regex',
  'by word': 'Whole word',
  'replace': 'Replace',
  'replace all': 'Replace all',
  'close': 'Close',
};

export const NoteEditor = (
  { value, onChange, autoFocus, ref }: {
    value: string,
    onChange: (value: string) => void,
    autoFocus?: boolean,
    ref?: Ref<EditorHandle>,
  },
) => {
  const host = useRef<HTMLDivElement>(null);
  const view = useRef<EditorView>(null);
  const changed = useRef(onChange);
  changed.current = onChange;
  // the text as last reported through onChange, to know it coming back
  const reported = useRef(value);
  const [menuAt, setMenuAt] = useState<{ top: number, left: number } | null>(
    null,
  );

  useEffect(() => {
    const editor = new EditorView({
      parent: host.current!,
      state: EditorState.create({
        doc: value,
        // at the start, past any block there, so it opens drawn
        selection: { anchor: startOutsideBlocks(blockKinds, value) },
        extensions: [
          history(),
          // ahead of the default keymap, so its Enter comes first on an item
          checklists,
          keymap.of([...defaultKeymap, ...historyKeymap, ...searchKeymap]),
          search({ top: true }),
          EditorState.phrases.of(searchPhrases),
          EditorView.lineWrapping,
          EditorView.contentAttributes.of({ spellcheck: 'false' }),
          placeholder('Write…'),
          shownBlocks,
          calcs,
          links,
          EditorView.domEventHandlers({
            // a right click away from the selection moves the cursor there
            // first, so what the menu inserts lands where it was opened. A
            // phone keeps its own menu, for copy and paste; what ours offers
            // is in the note's menu there
            contextmenu: (e, editor) => {
              if (isMobile()) return false;
              e.preventDefault();
              const pos = editor.posAtCoords({ x: e.clientX, y: e.clientY });
              const { from, to } = editor.state.selection.main;
              if (pos !== null && (pos < from || pos > to)) {
                editor.dispatch({ selection: { anchor: pos } });
              }
              setMenuAt({ top: e.clientY, left: e.clientX });
              return true;
            },
          }),
          EditorView.updateListener.of(update => {
            if (
              update.docChanged
              && !update.transactions.some(tr => tr.annotation(external))
            ) {
              reported.current = update.state.doc.toString();
              changed.current(reported.current);
            }
          }),
        ],
      }),
    });
    view.current = editor;
    if (autoFocus) editor.focus();
    return () => editor.destroy();
    // the editor is made once per note; later values arrive below
  }, []);

  // a note sync replaced underneath; the editor's own text coming back is
  // the same string, so a large note isn't serialised again per keystroke
  useEffect(() => {
    const editor = view.current;
    if (!editor || value === reported.current) return;
    if (editor.state.doc.toString() === value) return;
    editor.dispatch({
      changes: { from: 0, to: editor.state.doc.length, insert: value },
      annotations: external.of(true),
    });
    reported.current = value;
  }, [value]);

  // on a line of its own below the cursor's, with the cursor after it
  const insertBlock = (block: string) => {
    const editor = view.current;
    if (!editor) return;
    const line = editor.state.doc.lineAt(editor.state.selection.main.head);
    const blank = !line.text.trim();
    const insert = blank ? block : `\n${block}`;
    const from = blank ? line.from : line.to;
    editor.dispatch({
      changes: { from, to: blank ? line.to : from, insert },
      selection: { anchor: from + insert.length },
    });
    editor.focus();
  };

  const insertSheet = () => insertBlock(newSheet);
  const insertRecipe = () => insertBlock(newRecipe);
  const insertCalc = () => insertBlock(newCalc);

  // Each line the selection touches made an item, or back into a plain line
  // if all already are. With just a cursor, it goes to the end of its line
  const insertChecklist = () => {
    const editor = view.current;
    if (!editor) return;
    const { state } = editor;
    const { main } = state.selection;
    const first = state.doc.lineAt(main.from).number;
    const last = state.doc.lineAt(main.to).number;
    const lines = Array.from(
      { length: last - first + 1 },
      (_, i) => state.doc.line(first + i),
    );
    const allItems = lines.every(l => parseItem(l.text));
    const changes = state.changes(
      lines
        .filter(l => Boolean(parseItem(l.text)) === allItems)
        .map(l => ({ from: l.from, to: l.to, insert: toggleItem(l.text) })),
    );
    editor.dispatch({
      changes,
      selection: main.empty
        ? EditorSelection.cursor(changes.apply(state.doc).line(first).to)
        : main.map(changes),
    });
    editor.focus();
  };

  const checklistDone = () => {
    const editor = view.current;
    const items = editor ? checklistItems(editor.state) : [];
    return items.length ? items.every(i => i.checked) : null;
  };

  const checkAll = (checked: boolean) => {
    const editor = view.current;
    if (!editor) return;
    editor.dispatch({
      changes: checkAllItems(checklistItems(editor.state), checked),
      userEvent: 'input.checklist',
    });
  };

  // in place of the selection, with the cursor after it
  const insertDateTime = () => {
    const editor = view.current;
    if (!editor) return;
    editor.dispatch(editor.state.replaceSelection(formatDateTime(new Date())));
    editor.focus();
  };

  const find = () => {
    const editor = view.current;
    if (editor) openSearchPanel(editor);
  };

  const closeMenu = (refocus: boolean) => {
    setMenuAt(null);
    if (refocus) view.current?.focus();
  };

  useImperativeHandle(ref, () => ({
    focus: () => view.current?.focus(),
    insertSheet,
    insertRecipe,
    insertCalc,
    insertChecklist,
    checklistDone,
    checkAll,
    insertDateTime,
    find,
  }), []);

  // the note's checkboxes as the menu opens
  const done = menuAt ? checklistDone() : null;

  return (
    <>
      <div className="note-body" ref={host} />
      {menuAt && (
        <PopupMenu at={menuAt} onClose={closeMenu}>
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              insertSheet();
            }}>
            Insert table
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              insertRecipe();
            }}>
            Insert recipe
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              insertCalc();
            }}>
            Insert calculation
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              insertChecklist();
            }}>
            Checklist
          </button>
          {done !== null && (
            <button
              role="menuitem"
              onClick={() => {
                setMenuAt(null);
                checkAll(!done);
              }}>
              {done ? 'Uncheck all' : 'Check all'}
            </button>
          )}
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              insertDateTime();
            }}>
            Date & Time
          </button>
          <button
            role="menuitem"
            onClick={() => {
              setMenuAt(null);
              find();
            }}>
            Find in note
          </button>
        </PopupMenu>
      )}
    </>
  );
};
